// Every board frame is as tall as its page, so the board page scrolls and a frame never does: a block reads as
// part of the board. The frame's origin is opaque, so the parent can't measure it: a reporter inside the frame
// posts the page's height. The parent adds it to the frame's document with the page's nonce, like the render
// check's probe. It keeps a token of its frame's own in its closure and takes its script element out of the
// page before any page script runs, so a page can't post a height the parent takes. A height is still only a
// layout hint, clamped between the limits below: a block's frame is shorter than a whole page's.

export const FRAME_HEIGHT_MESSAGE = 'sparck:height'
export const FRAME_MIN_HEIGHT = 60
export const BLOCK_FRAME_MAX_HEIGHT = 5000
export const PAGE_FRAME_MAX_HEIGHT = 20000
// Until the frame reports.
export const FRAME_FIRST_HEIGHT = 600

export const clampFrameHeight = (value: unknown, maxHeight: number): number | null => {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null

  return Math.min(Math.max(Math.ceil(value), FRAME_MIN_HEIGHT), maxHeight)
}

// A page that grows because its frame did comes out taller than the frame on every report, by the same step,
// up to the frame's limit: min-height: 100vh does it, and the block checks refuse that and the other sizes
// taken from the window's height, but a page can still find another way. The frame follows its page, but
// after FRAME_LOOP_STEPS reports in a row that each grew it by the same step, give or take a pixel, each within
// FRAME_LOOP_MS of the height before it, it goes back to its height before them and holds it. Once the page has
// been quiet for FRAME_SETTLE_MS, the frame tries the page's latest height once: a page that grows by the same
// step again right away is looping, and the frame goes back and holds for good; any other growth was the
// page's own, such as an animation, and the frame follows the page again.
export const FRAME_LOOP_STEPS = 5
export const FRAME_LOOP_MS = 500
export const FRAME_SETTLE_MS = 1500

type Streak = { from: number; step: number; count: number }
type Hold = { from: number; step: number; pending: number | null; trying: boolean; forGood: boolean }

export type FrameHeight = { height: number; changedAt: number; streak: Streak | null; hold: Hold | null }

export const firstFrameHeight = (now: number): FrameHeight => ({
  height: FRAME_FIRST_HEIGHT,
  changedAt: now,
  streak: null,
  hold: null,
})

const sameStep = (step: number, other: number) => Math.abs(step - other) <= 1

const grownStreak = (state: FrameHeight, step: number, now: number): Streak | null => {
  if (step <= 0) return null
  if (state.streak && now - state.changedAt <= FRAME_LOOP_MS && sameStep(step, state.streak.step)) {
    return { ...state.streak, count: state.streak.count + 1 }
  }

  return { from: state.height, step, count: 1 }
}

const heldHeight = (state: FrameHeight, hold: Hold, next: number, now: number): FrameHeight => {
  if (hold.forGood) return state
  if (hold.trying && now - state.changedAt <= FRAME_LOOP_MS && sameStep(next - state.height, hold.step)) {
    return { ...state, height: hold.from, changedAt: now, hold: { ...hold, pending: null, trying: false, forGood: true } }
  }

  return { ...state, hold: { ...hold, pending: next, trying: false } }
}

// The frame's next state for a height its page reported at `now`.
export const followFrameHeight = (
  state: FrameHeight,
  value: unknown,
  now: number,
  maxHeight: number,
): FrameHeight => {
  const next = clampFrameHeight(value, maxHeight)
  if (next === null || next === state.height) return state
  if (state.hold) return heldHeight(state, state.hold, next, now)

  const streak = grownStreak(state, next - state.height, now)
  if (streak && streak.count >= FRAME_LOOP_STEPS) {
    const hold = { from: streak.from, step: streak.step, pending: next, trying: false, forGood: false }

    return { height: streak.from, changedAt: now, streak: null, hold }
  }

  return { height: next, changedAt: now, streak, hold: null }
}

// Once a held frame's page has been quiet for FRAME_SETTLE_MS: its latest height is tried, and a try that
// brought no growth back lets the frame follow its page again.
export const settleFrameHeight = (state: FrameHeight, now: number): FrameHeight => {
  const { hold } = state
  if (!hold || hold.forGood) return state
  if (hold.trying || hold.pending === null) return { ...state, streak: null, hold: null }

  return { ...state, height: hold.pending, changedAt: now, hold: { ...hold, pending: null, trying: true } }
}

// Plain ES5 source, never compiled, like the probe. The height is the root element's box, or the body's
// content with its margins when that is taller (content that overflows a root styled to the frame's height),
// or, when the page overflows the frame at all, everything it would scroll to. It reports on load and whenever
// the root or the body changes size, and only a height it hasn't reported last.
export const heightReporter = ({ token }: { token: string }) =>
  String.raw`(function () {
  'use strict';
  var TOKEN = ${JSON.stringify(token)}, TYPE = ${JSON.stringify(FRAME_HEIGHT_MESSAGE)};
  var self = document.currentScript;
  if (self && self.parentNode) self.parentNode.removeChild(self);

  var parentWindow = window.parent, last = -1, observer = null, watchingBody = false;

  function measure() {
    var root = document.documentElement, body = document.body, height = root.getBoundingClientRect().height, style;
    if (body) {
      style = getComputedStyle(body);
      height = Math.max(height, body.scrollHeight + (parseFloat(style.marginTop) || 0) + (parseFloat(style.marginBottom) || 0));
    }
    if (root.scrollHeight > root.clientHeight + 1) height = Math.max(height, root.scrollHeight);
    return Math.ceil(height);
  }
  function watchBody() {
    if (!observer || watchingBody || !document.body) return;
    watchingBody = true;
    observer.observe(document.body);
  }
  function report() {
    var height;
    watchBody();
    try { height = measure(); } catch (e) { return; }
    if (!(height >= 0) || height === last) return;
    last = height;
    parentWindow.postMessage({ type: TYPE, token: TOKEN, height: height }, '*');
  }

  if (typeof ResizeObserver === 'function') {
    observer = new ResizeObserver(report);
    observer.observe(document.documentElement);
  }
  document.addEventListener('DOMContentLoaded', watchBody);
  window.addEventListener('load', report);
})();`
